/**
 * validation.js — pure required-field computation for the V2 form.
 *
 * Extracted from useBookingForm.js in refactor R-2 (2026-07-14).
 * Zero behaviour change. The hook now calls `computeMissing(values,
 * fieldOverrides)` inside a useMemo instead of inlining the loop.
 *
 * Mirrors the server-side rules in `lbs_enforce_required_fields` +
 * the Phase 9.3 driver-TBA bypass.
 */
import { FIELDS } from "../registry/fields";
import { applyFieldOverrides } from "@/lib/api";

// Tabs the tenant switched off — required fields there can't block a save.
const hiddenTabs = (overrides) => new Set(
  (overrides || []).filter((o) => o.scope === "tab" && o.is_visible === false).map((o) => o.field_key)
);

const DRIVER_KEYS = new Set([
  "assigned_driver_id", "driver_name", "driver_phone",
  "driver_licence_type", "driver_licence_number", "driver_licence_state",
]);

/**
 * @param {object} values          current form values
 * @param {Array}  fieldOverrides  per-tenant override rows (may be empty)
 * @returns {{tab:string,label:string,key?:string}[]} missing entries
 */
export function computeMissing(values, fieldOverrides) {
  const m = [];
  const tba = !!values.driver_tba;

  // (a) Legacy hardcoded rules — kept verbatim from the pre-R-2 hook.
  if (!values.site_id)          m.push({ tab: "schedule", label: "Site" });
  if (!values.bay_id)           m.push({ tab: "schedule", label: "Bay" });
  if (!values.direction)        m.push({ tab: "schedule", label: "Direction" });
  if (!values.booking_date)     m.push({ tab: "schedule", label: "Date" });
  if (!values.start_time)       m.push({ tab: "schedule", label: "Start time" });
  if (!values.end_time)         m.push({ tab: "schedule", label: "End time" });
  if (!values.vehicle_type)     m.push({ tab: "vehicle",  label: "Vehicle type" });
  const effFields = applyFieldOverrides(FIELDS, fieldOverrides);
  const regoTab = effFields.find((f) => f.key === "rego_prime_mover")?.tab || "vehicle";
  if (!values.rego_prime_mover) m.push({ tab: regoTab, label: "Prime mover rego" });
  if (!tba && !values.assigned_driver_id) m.push({ tab: "driver", label: "Driver" });

  // (b) Dynamically-required fields from per-tenant overrides.
  const hidden = hiddenTabs(fieldOverrides);
  for (const f of effFields) {
    if (!f.required || f.alwaysRequired || hidden.has(f.tab)) continue;
    if (tba && DRIVER_KEYS.has(f.key)) continue;    // Phase 9.3 bypass
    const v = values[f.key];
    const isEmpty = v === undefined || v === null || v === "" ||
                    (Array.isArray(v) && v.length === 0);
    if (isEmpty) m.push({ tab: f.tab, label: f.label, key: f.key });
  }
  return m;
}
